import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder } from '@angular/forms';
import { StorageService } from '@morningstar/shared/base/storage-service';
import { select } from '@morningstar/shared/operators';
import { combineLatest, merge, Subject } from 'rxjs';
import { delay, filter, map, startWith, tap } from 'rxjs/operators';
import { DashboardBaseComponent } from '../../dashboard-base/dashboard-base.component';
import { CheckInsSelectors } from '../check-ins.selectors';
import { CheckInsService } from '../check-ins.service';
import { DayOfWeekCheckIns } from './day-of-week-check-ins';

@Component({
  selector: 'wsg-day-of-week-check-ins',
  templateUrl: './day-of-week-check-ins.component.html',
  styleUrls: ['./day-of-week-check-ins.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DayOfWeekCheckInsComponent extends DashboardBaseComponent implements AfterViewInit, OnDestroy {

  // == CONSTANTS =======================================================

  chartId = 'dayOfWeekCheckIns';
  title = 'Day Of Week Check-Ins';
  initialChartSettings = {
    legendSettings: { visible: true, position: 'Top' },
    primaryXAxis: { valueType: 'Category' },
    primaryYAxis: { rangePadding: 'None', minimum: 0, maximum: 100, interval: 20, lineStyle: { width: 0 }, majorTickLines: { width: 0 }, minorTickLines: { width: 0 } },
    chartArea: { border: { width: 0 } },
    marker: { visible: true, height: 10, width: 10 },
    tooltip: { enable: true }
  };

  // == STATE ===========================================================
 
  form = this.formBuilder.group({
    location: ''
  });
 
  state$ = this.checkInsService.state$;

  // == ACTIONS =========================================================

  afterViewInit = new Subject();
  afterViewInit$ = this.afterViewInit.asObservable();
 
  // == INTERMEDIATE ====================================================
   
  location$ = this.location.valueChanges.pipe(
    startWith('')
  );
   
  checkIns$ = this.state$.pipe(select<DayOfWeekCheckIns[]>(CheckInsSelectors.dayOfWeekCheckIns));

  chartSettings$ = this.checkIns$.pipe(
    map(checkIns => {
      const maxCheckIns = this.getYAxisMaximum(checkIns);
      
      const settings = this.initialChartSettings;

      settings.primaryYAxis.maximum = maxCheckIns;
      settings.primaryYAxis.interval = maxCheckIns / 5;

      console.log("settings");
      return settings;
    })
  );
   
  // == SIDE EFFECTS ====================================================
 
  refreshChart$ = combineLatest([this.checkIns$, this.afterViewInit$, this.theme$]).pipe(
    delay(50),
    filter(_ => !!this.chart),
    tap(([checkIns, _]) => {
      this.chart.dataSource = checkIns;
      this.chart.refresh();
    })
  );

  onGetCheckIns$ = this.location$.pipe(
    filter(_ => !!this.location.value),
    tap(_ => this.checkInsService.getDayOfWeekCheckIns(this.location.value))
  );
 
  // == SUBSCRIPTION ====================================================
 
  subscription = merge(
    this.onGetCheckIns$,
    this.refreshChart$
  ).subscribe();
 
  // == LIFECYCLE =======================================================
 
  constructor(private checkInsService: CheckInsService,
              private formBuilder: FormBuilder,
              protected storageService: StorageService) {
    super(storageService);
  };

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngAfterViewInit() {
    this.afterViewInit.next();
  }
 
  // == HELPERS =========================================================
 
  get location(): AbstractControl {
    return this.form.get('location');
  }

  getYAxisMaximum(checkIns: DayOfWeekCheckIns[]): number {
    const maxGuestCheckIns = Math.max(...checkIns.map(c => c.guestCount));
    const maxMemeberCheckIns = Math.max(...checkIns.map(c => c.memberCount));

    let maxCheckIns = maxGuestCheckIns > maxMemeberCheckIns ? maxGuestCheckIns : maxMemeberCheckIns;

    if (maxCheckIns < 10) {
      maxCheckIns = 10;
    } else {
      maxCheckIns += 5;
    }

    return maxCheckIns;
  }
}
